import React, { useState } from "react"
import Layout from "../../../components/layouts"
import { Link } from "gatsby"
import FsLightbox from "fslightbox-react"
import Banner from '../../../components/features/Banner'
import Question from '../../../components/features/Question'
import Video from '../../../components/features/Video'
import Info from '../../../components/features/Info'
import Steps from '../../../components/features/Steps'
import SEO from '../../../components/common/SEO';
import FeatureCards from "../../../components/features/FeatureCards"

export const query = graphql`
  query SafetyTraining {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
      }
    }
  }
  `

export default ({data}) => {
  const [toggler, setToggler] = useState(false)
    const lightboxProps = {
        toggler: toggler,
        setToggler: setToggler,
        sources: ["https://www.youtube.com/watch?v=nC17rj0ISxs"],
    }

    const bannerProps = {
        title: 'Online Training Courses',
        subtitle: `Choose from over 80 pre-built safety awareness courses or 
        create your own custom cloud course. Assure full comprehension with lesson 
        selection, custom quiz creation, adjustable pass rates, and integration with 
        compliance components across Workhub. Training workers has never been easier!`,
        featureName: 'Training',
        rightButtonText: 'View Course List',
        rightButtonSrc: '/features/training/',
        mockupImg: '/features/training-courses/feature-page-header-custom-training.png'
    }

    const questionProps = {
        question: 'Are your training manuals being used as a pillow?',
        answer: `Properly training workers has been a struggle since the dawn 
        of time, and even though it received a major update with the introduction 
        of video training, it’s still challenging to properly host, quiz, and track 
        worker training. Some of the common training issues Workhub set out to solve are:`,
        answerBulletList: [
            'high costs to host training sessions on-site or in class',
            'challenges in training large groups and comprehension testing',
            'difficulty tracking worker training progress',
            'motivating workers to take optional training can be tough!',
        ],
        imageSrc: '/features/sketch/training-large.jpg',
        imgAlt: 'Cartoon of man teaching a Safety Course in a oldschool classroom setting with a bored student',
        isImageLeft: true
    }

    const videoProps = {
        videoSrc: 'https://www.youtube.com/watch?v=xE2yXvWB038',
        videoImage: '/features/training-courses/training-courses-thumbnail.jpg',
        videoStatement: (
          <strong>Free Online Training Courses</strong>)
    }

    const infoProps = {
        imageLeftToRight: false,
        infoList: [
            {
                mockupImage: '/features/training-courses/course-library-small.png',
                infoTitle: 'Hand-pick your course library',
                infoSubtitle: `Browse from Workhub’s library of over 80 pre-built safety awareness 
                courses, covering topics like WHMIS, fire safety, transportation of dangerous goods 
                (TDG), workplace violence and harassment, and wildlife awareness. Choose which courses 
                apply to your workers and assign them based on position and roles.`,
                infoBullets: [
                    {
                        name: 'Pick and choose your courses:',
                        description: `add and remove courses from your organization’s 
                        library as needed`
                    },
                    {
                        name: 'Customize your settings:',
                        description: `assign your courses to workers, set requirement 
                        levels, expiration dates, pass rates, and connections to 
                        certificates for automatic uploads, plus many more choices`,
                    },
                    {
                        name: 'Review compliance in a single glance:',
                        description: `compliance reports are available for individual 
                        courses or for multiple courses in simple, easy to read formats`,
                    }
                ],
            },
            {
                mockupImage: '/features/training-courses/training-course-admin-custom.png',
                infoTitle: 'Create and Host Custom Courses with Ease',
                infoSubtitle: `Create custom courses that instruct your workers on subjects 
                tailored precisely to your organization’s demands and pair them with quizzes 
                that drill down to exactly what your workers need to excel.`,
                infoBullets: [
                    {
                        name: 'Build your own courses:',
                        description: `choose the content you want your workers to learn 
                        with, including file uploads, website links, and embedded YouTube video`
                    },
                    {
                        name: 'Break courses into lessons:',
                        description: `compile your courses from laser-focused lessons targeting 
                        your desired topics`,
                    },
                    {
                        name: 'Ask the right questions:',
                        description: `create quiz questions to test your 
                        workers’ comprehension for each lesson. From simple true-or-false questions 
                        to in-depth multiple choice, you can test how you see fit!`,
                    }
                ],
            },
            {
                mockupImage: '/features/training-courses/training-course-admin-compliance.jpg',
                infoTitle: 'Training is a breeze for workers',
                infoSubtitle: `Your chosen training will appear for workers on their dashboard, with 
                required courses appearing first and optional courses appearing second. Workers can easily 
                choose their course, sail through a lesson, and challenge the quiz when they are ready. Their 
                completion will grant a certificate that can be downloaded after passing the quiz, and can 
                be set to automatically populate a linked Certificate type as proof of training.`,
                infoBullets: [
                    {
                        name: 'Ease of access:',
                        description: `workers can complete training anywhere - at a desk, on a phone, or using a tablet`
                    },
                    {
                        name: 'Prove yourself:',
                        description: `full-sized certificates and convenient wallet cards can be downloaded and printed to keep proof of training on workers at all times`,
                    },
                    {
                        name: 'Notifications:',
                        description: `workers and admins will be notified ahead of time that training is coming due, so they’re never caught unaware`,
                    }
                ],
            }
        ]
    }

    const seo = { 
        title: 'Workhub | 80+ Free Online Safety Training Courses',
        description: 'We offer 80+ free online safety training courses with our safety management software. Custom courses, compliance tracking & training courses for a wide-range of industries.',
        image: '/features/training-courses/social-card.jpg',
    }

    const featureIndex = [
        {
          title: "Over 80+ courses",
          para: "Choose from our library of pre-built safety awareness courses.",
          link: '/features/training/',
        },
        {
          title: "Customize Your Courses",
          para: "Set expiration dates, pass rates, and let workers challenge quizzes + many more options available for each course.",
        },
        {
          title: "Assign by role",
          para: "Add course requirements based on job role. New workers can easily see what is required of them.",
        },
        {
            title: "Designate Optional/Required",
            para: "Decide which courses you want to make required for your workers and which ones you want to make optional.",
          },
          {
            title: "Notifications",
            para: "Your safety team and your workers will be notified when course expirations are coming due.",
          },
          {
            title: "Compliance Monitoring",
            para: "Training dates, expirations, and quiz results are all tracked in Workhub.",
          },
          {
            title: "Get Feedback",
            para: "Get feedback on the courses to gain valuable insight from your workers.",
          },
      ]

  return (
    <Layout {...lightboxProps}>
        <SEO {...seo} {...data.site}/>
        <Banner {...bannerProps} {...lightboxProps}/>
        <Question {...questionProps} />
        <Video {...videoProps} {...lightboxProps} />
        <Info {...infoProps} />

             {/* FEATURE OVERVIEW */}

             <section className="feature-overview-container">
          <h2>Overview</h2>

          <div className="feature-overview-grid" style={{marginBottom: '60px'}}>
            {featureIndex.map(item => (
              <FeatureCards title={item.title} para={item.para} link={item.link} />
            ))}
          </div>
        </section>

    </Layout>
  )
}
